import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Masonry from 'react-masonry-css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaTimes, FaPlus, FaChevronUp, FaShoppingBasket, FaAppleAlt, FaAngleDoubleUp, FaEye, FaEyeSlash, FaEdit } from 'react-icons/fa';
import { BasketCard } from '../BasketCard/BasketCard';
import ItemCard from '../ItemCard';
import AddItemModal from '../AddItemModal/AddItemModal';
import AddBasketModal from '../AddBasketModal';
import ErrorBoundary from '../ErrorBoundary';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchWorkspaceDetails, 
  addBasket, 
  addTopic, 
  deleteTopic, 
  deleteBasket, 
  setBasketContentChanged, 
  setCurrentBasket, 
  updateBasket,
  updateBasketInState,
  setBaskets,
  selectCurrentWorkspace,
  setWorkspaceLoaded,
  createBasket,
  addBasketToWorkspace,
  moveBasketToTop,
} from '../../store/workspacesSlice';
import EditBasketModal from './EditBasketModal';
import { Switch } from '@headlessui/react';
import OfflineStatusBanner from '../OfflineStatusBanner/OfflineStatusBanner';
import TabButton from '../common/TabButton';
import ScrollableFilter from './ScrollableFilter';
import StudyModeSwitch from './StudyModeSwitch';
import StudyModeItemCard from './StudyModeItemCard';
import WorkspaceStats from './WorkspaceStats';
import WorkspaceDetailSkeleton from './WorkspaceDetailSkeleton';
import { Lock } from 'lucide-react';
import { usePremium } from '../Subscription/PremiumProvider';
/**
 * Helper function to capitalize the first letter of each word in a string
 * @param {string} string - The input string to capitalize
 * @returns {string} The capitalized string
 */
const capitalizeFirstLetter = (string) => {
  return string
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Constants for filtering baskets based on number of items
 */
const BASKET_FILTERS = {
  ALL: 'All Baskets',
  SINGLE: 'Single Item',
  DOUBLE: 'Two Items',
  NO_MASTERY: 'No Mastery (0%)',
  MULTIPLE: 'Three or More',
  LOW_MASTERY: 'Low Mastery (<50%)',
  HIGH_MASTERY: 'High Mastery (>50%)',
  FULL_MASTERY: 'Full Mastery (100%)',
  EMPTY: 'Empty'
};

/**
 * Constant used as default value for tag filtering
 */
const ALL_TAGS_FILTER = 'All Items';

/**
 * Move this helper function to the top, right after the constants
 */
const calculateBasketMastery = (basket) => {
  if (!basket.topics?.length) return 0;
  const masteredTopics = basket.topics.filter(topic => topic.is_master).length;
  return (masteredTopics / basket.topics.length) * 100;
};

// Add helper to check if filter is premium
const isPremiumFilter = (filter) => {
  const premiumFilters = [
    BASKET_FILTERS.LOW_MASTERY,
    BASKET_FILTERS.HIGH_MASTERY,
    BASKET_FILTERS.FULL_MASTERY
  ];
  return premiumFilters.includes(filter);
};

/**
 * Main component for displaying workspace details including baskets and topics
 * Handles searching, filtering, and study mode functionality
 */
const WorkspaceDetail = () => {
  const { workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { activeTab: stateActiveTab, workspaceName: stateWorkspaceName, searchTerm: stateSearchTerm } = location.state || {};

  const [workspaceName, setWorkspaceName] = useState(() => {
    return stateWorkspaceName || 'Workspace';
  });

  const [topicName, setTopicName] = useState('');

  const [isAddBasketModalOpen, setIsAddBasketModalOpen] = useState(false);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(stateActiveTab || 'Baskets');
  const [searchTerm, setSearchTerm] = useState(() => {
    return location.state?.searchTerm || '';
  });
  const [editingBasket, setEditingBasket] = useState(null);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const workspace = useSelector(selectCurrentWorkspace);
  const { baskets, topics, status, error, tags, isWorkspaceLoaded } = workspace;

  // Add this line to create a ref for the search input
  const searchInputRef = useRef(null);

  // Add new state for basket filter
  const [basketFilter, setBasketFilter] = useState(BASKET_FILTERS.ALL);

  // Add these new state variables inside the WorkspaceDetail component
  const [activeTagFilter, setActiveTagFilter] = useState(ALL_TAGS_FILTER);
  const [isStudyMode, setIsStudyMode] = useState(() => {
    return JSON.parse(localStorage.getItem('isStudyMode') || 'true');
  });
  const [revealedBaskets, setRevealedBaskets] = useState(new Set());
  const [revealedTopics, setRevealedTopics] = useState(new Set());

  /**
   * Effect to fetch workspace details when component mounts or workspaceId changes
   */
  useEffect(() => {
    if (workspaceId && !isWorkspaceLoaded) {
      console.log("Fetching workspace details...");
      dispatch(fetchWorkspaceDetails(workspaceId));
    }
  }, [workspaceId, dispatch, isWorkspaceLoaded]);

  useEffect(() => {
    console.log('Workspace state updated:', { baskets, topics, tags, status, error });
  }, [baskets, topics, tags, status, error]);

  // Replace the previous useEffect for focusing with this one
  useEffect(() => {
    const focusSearchInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
        if (location.state?.searchTerm) {
          searchInputRef.current.value = location.state.searchTerm;
        }
      }
    };

    // Focus immediately
    focusSearchInput();

    // Also focus after a short delay to ensure the input is rendered
    const timeoutId = setTimeout(focusSearchInput, 100);

    return () => clearTimeout(timeoutId);
  }, [workspaceId, location.state?.searchTerm]); // Add searchTerm as dependency

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 200px
      const scrolled = window.scrollY > 200;
      setShowScrollTop(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleAddMenu = (e) => {
    e.stopPropagation();
    setIsAddMenuOpen(!isAddMenuOpen);
  };

  const handleOpenBasket = (basketId, basket, workspaceId, workspaceName) => {
    console.log('Opening basket:', basketId, basket, workspaceId, workspaceName);
    if (!basketId || !basket) {
      console.error('Invalid basket data:', { basketId, basket });
      return;
    }
    
    dispatch(setCurrentBasket(basket));
    navigate(`/basket/${basketId}`, {
      state: { 
        basket, 
        workspaceId,
        workspaceName,
        fromWorkspace: true
      }
    });
  };

  /**
   * Handles adding a new basket to the workspace
   * @param {Object} basketData - The basket data to be added
   */
  const handleAddBasket = async (basketData) => {
    if (!workspaceId) {
      console.error('Cannot add basket: workspaceId is undefined');
      return;
    }

    try {
      // Single source of truth for basket creation
      const result = await dispatch(createBasket(basketData)).unwrap();
      
      if (result) {
        toast.success('Basket added successfully');
        setIsAddBasketModalOpen(false);
      }
    } catch (error) {
      console.error('Error adding basket:', error);
      toast.error('Failed to add basket: ' + error.message);
      throw error; // Propagate error back to modal
    }
  };

  const handleAddItem = async (newTopicData) => {
    try {
      console.log('Adding new topic with data:', newTopicData);
      const result = await dispatch(addTopic({
        ...newTopicData,
        workspace_id: workspaceId,
        tag_id: newTopicData.tag?.id,
        tag: newTopicData.tag ? {
          id: newTopicData.tag.id,
          name: newTopicData.tag.name,
          color: newTopicData.tag.color,
          workspace_id: workspaceId
        } : null
      })).unwrap();
      
      if (result) {
        toast.success('Topic added successfully');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to add topic: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await dispatch(deleteTopic({ workspaceId, topicId: itemId }));
      toast.success('Topic deleted successfully');
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Failed to delete topic: ' + error.message);
    }
  };

  const handleDeleteBasket = async (basketId) => {
    try {
      await dispatch(deleteBasket({ workspaceId, basketId }));
      toast.success('Basket deleted successfully');
    } catch (error) {
      console.error('Error deleting basket:', error);
      toast.error('Failed to delete basket: ' + error.message);
    }
  };

  const handleBasketMenuClick = (basket, action) => {
    switch (action) {
      case 'edit':
        setEditingBasket(basket);
        break;
      case 'delete':
        handleDeleteBasket(basket.id);
        break;
      case 'share':
        console.log('Share basket', basket);
        // Implement share functionality
        break;
      default:
        console.log('Unknown action', action);
    }
  };

  const handleSaveBasketEdit = async (updatedData) => {
    try {
      console.log('Saving basket edit:', { basketId: editingBasket.id, updatedData, workspaceId });
      const result = await dispatch(updateBasket({ 
        basketId: editingBasket.id, 
        updatedData, 
        workspaceId 
      })).unwrap();
      
      console.log('Update basket result:', result);
      
      if (result) {
        toast.success('Basket updated successfully');
        setEditingBasket(null);
        
        // Update the local state to reflect the changes immediately
        const updatedBaskets = baskets.map(basket => 
          basket.id === result.id ? { ...basket, ...result } : basket
        );
        dispatch(setBaskets(updatedBaskets));
        
        // Update the local state using the updateBasketInState action
        dispatch(updateBasketInState(result));
      } else {
        throw new Error('Failed to update basket: No data returned');
      }
    } catch (error) {
      console.error('Error updating basket:', error);
      toast.error(`Failed to update basket: ${error.message}`);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    // Focus the search input after clearing
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  /**
   * Memoized sorted baskets based on updated_at/created_at timestamps
   */
  const sortedBaskets = useMemo(() => {
    if (!baskets?.length) return [];
    
    return [...baskets].sort((a, b) => {
      // Sort by most recent date first
      const dateA = new Date(a.updated_at || a.created_at || 0);
      const dateB = new Date(b.updated_at || b.created_at || 0);
      return dateB - dateA;
    });
  }, [baskets]);

  /**
   * Handles filtering baskets based on search term and basket filter
   * Prioritizes matches in basket names, then topic names, then concepts
   */
  const filteredBaskets = useMemo(() => {
    if (!sortedBaskets) return [];
    
    // Separate matches by priority
    const directNameMatches = [];
    const topicMatches = [];
    const conceptMatches = [];
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // First filter by mastery if a mastery filter is selected
    const masteryFiltered = sortedBaskets.filter(basket => {
      const masteryPercentage = calculateBasketMastery(basket);
      
      switch (basketFilter) {
        case BASKET_FILTERS.NO_MASTERY:
          return masteryPercentage === 0;
        case BASKET_FILTERS.LOW_MASTERY:
          return masteryPercentage > 0 && masteryPercentage < 50;
        case BASKET_FILTERS.HIGH_MASTERY:
          return masteryPercentage >= 50 && masteryPercentage < 100;
        case BASKET_FILTERS.FULL_MASTERY:
          return masteryPercentage === 100;
        default:
          // For non-mastery filters, continue with existing logic
          const itemCount = basket.topics?.length || 0;
          switch (basketFilter) {
            case BASKET_FILTERS.SINGLE: return itemCount === 1;
            case BASKET_FILTERS.DOUBLE: return itemCount === 2;
            case BASKET_FILTERS.MULTIPLE: return itemCount >= 3;
            case BASKET_FILTERS.EMPTY: return itemCount === 0;
            default: return true;
          }
      }
    });
    
    // Then categorize matches within mastery-filtered baskets
    masteryFiltered.forEach(basket => {
      if (!basket) return;
      
      if (basket.name?.toLowerCase().includes(searchTermLower)) {
        directNameMatches.push(basket);
      } else if (basket.topics?.some(topic => 
        topic?.name?.toLowerCase().includes(searchTermLower)
      )) {
        topicMatches.push(basket);
      } else if (basket.topics?.some(topic =>
        topic?.concepts?.some(concept =>
          concept?.text?.toLowerCase().includes(searchTermLower)
        )
      )) {
        conceptMatches.push(basket);
      }
    });
    
    return [...directNameMatches, ...topicMatches, ...conceptMatches];
  }, [sortedBaskets, searchTerm, basketFilter]);

  // Modify the getFilterCounts function to handle mastery counts
  const getFilterCounts = useCallback(() => {
    if (!sortedBaskets) return {};
    
    // First filter baskets by search term
    const searchFiltered = sortedBaskets.filter(basket => {
      if (!basket) return false;
      
      if (!searchTerm.trim()) return true;
      
      const searchTermLower = searchTerm.toLowerCase();
      
      // Check basket name
      if (basket.name?.toLowerCase().includes(searchTermLower)) return true;
      
      // Check topics and concepts
      return basket.topics?.some(topic => {
        if (!topic) return false;
        
        if (topic.name?.toLowerCase().includes(searchTermLower)) return true;
        
        return topic.concepts?.some(concept => 
          concept?.text?.toLowerCase().includes(searchTermLower)
        );
      });
    });
    
    return {
      [BASKET_FILTERS.ALL]: searchFiltered.length,
      [BASKET_FILTERS.NO_MASTERY]: searchFiltered.filter(b => calculateBasketMastery(b) === 0).length,
      [BASKET_FILTERS.LOW_MASTERY]: searchFiltered.filter(b => {
        const mastery = calculateBasketMastery(b);
        return mastery > 0 && mastery < 50;
      }).length,
      [BASKET_FILTERS.HIGH_MASTERY]: searchFiltered.filter(b => {
        const mastery = calculateBasketMastery(b);
        return mastery >= 50 && mastery < 100;
      }).length,
      [BASKET_FILTERS.FULL_MASTERY]: searchFiltered.filter(b => calculateBasketMastery(b) === 100).length,
      [BASKET_FILTERS.SINGLE]: searchFiltered.filter(b => (b.topics?.length || 0) === 1).length,
      [BASKET_FILTERS.DOUBLE]: searchFiltered.filter(b => (b.topics?.length || 0) === 2).length,
      [BASKET_FILTERS.MULTIPLE]: searchFiltered.filter(b => (b.topics?.length || 0) >= 3).length,
      [BASKET_FILTERS.EMPTY]: searchFiltered.filter(b => !b.topics?.length).length,
    };
  }, [sortedBaskets, searchTerm]);

  const sortedTopics = useMemo(() => {
    if (!topics) return [];
    
    return [...topics].sort((a, b) => {
      if (!a || !b) return 0;
      // Sort by updated_at first, if it exists
      const aDate = a.updated_at || a.created_at || new Date(0);
      const bDate = b.updated_at || b.created_at || new Date(0);
      return new Date(bDate) - new Date(aDate); // Descending order (most recent first)
    });
  }, [topics]);

  // Add this new function inside the component
  const getTagFilterCounts = useCallback(() => {
    if (!sortedTopics) return {};
    
    // First filter topics by search term
    const searchFiltered = sortedTopics.filter(topic => {
      if (!topic) return false;
      
      if (!searchTerm.trim()) return true;
      
      const searchTermLower = searchTerm.toLowerCase();
      
      // Check topic name
      if (topic.name?.toLowerCase().includes(searchTermLower)) return true;
      
      // Check concepts
      return topic.concepts?.some(concept => 
        concept?.text?.toLowerCase().includes(searchTermLower)
      );
    });
    
    // Count items for each tag
    const counts = {
      [ALL_TAGS_FILTER]: searchFiltered.length
    };
    
    // Count items for each tag
    searchFiltered.forEach(topic => {
      if (topic.tag) {
        counts[topic.tag.name] = (counts[topic.tag.name] || 0) + 1;
      }
    });
    
    return counts;
  }, [sortedTopics, searchTerm]);

  // Modify the filteredTopics memo to include tag filtering
  const filteredTopics = useMemo(() => {
    if (!sortedTopics) return [];
    
    // First filter by tag if needed
    const tagFiltered = activeTagFilter === ALL_TAGS_FILTER
      ? sortedTopics
      : sortedTopics.filter(topic => topic.tag?.name === activeTagFilter);
    
    // Separate topics into two groups: direct matches and concept matches
    const directMatches = [];
    const conceptMatches = [];
    
    tagFiltered.forEach(topic => {
      if (!topic) return;
      
      const searchTermLower = searchTerm.toLowerCase();
      
      // Check topic name for direct match
      const nameMatch = topic.name?.toLowerCase().includes(searchTermLower);
      
      // Check concepts for matches
      const hasConceptMatch = topic.concepts?.some(concept => 
        concept?.text?.toLowerCase().includes(searchTermLower)
      ) || false;
      
      if (nameMatch) {
        directMatches.push(topic);
      } else if (hasConceptMatch) {
        conceptMatches.push(topic);
      }
    });
    
    // Combine the arrays with direct matches first, followed by concept matches
    return [...directMatches, ...conceptMatches];
  }, [sortedTopics, searchTerm, activeTagFilter]);

  // Add this new function to get unique tags from topics
  const getUniqueTags = useMemo(() => {
    if (!topics) return [];
    
    const tagsMap = new Map();
    
    topics.forEach(topic => {
      if (topic.tag) {
        tagsMap.set(topic.tag.name, topic.tag);
      }
    });
    
    return [ALL_TAGS_FILTER, ...Array.from(tagsMap.values())];
  }, [topics]);

  const handleBackNavigation = () => {
    dispatch(setWorkspaceLoaded(false));  // Set isWorkspaceLoaded to false
    navigate('/workspace');
  };

  const handleAddOption = (option) => {
    if (!workspaceId) {
      toast.error('Workspace ID is missing. Please try refreshing the page.');
      return;
    }

    if (option === 'basket') {
      setIsAddBasketModalOpen(true);
    } else if (option === 'item') {
      setIsAddItemModalOpen(true);
    }
    setIsAddMenuOpen(false);
  };

  const handleItemClick = (topic) => {
    navigate(`/topic-detail/${topic.id}`, {
      state: {
        topic,
        basket: topic.basket,
        fromBasket: false,
        searchTerm: searchTerm,
        workspaceId: workspaceId
      }
    });
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Add this new function to handle quick create
  const handleQuickCreate = () => {
    setTopicName(capitalizeFirstLetter(searchTerm)); // Capitalize the search term
    setIsAddItemModalOpen(true);
  };

  useEffect(() => {
    // If we have a basket ID to scroll to, find it and scroll
    const basketToScrollTo = location.state?.shouldScrollToBasket;
    if (basketToScrollTo) {
      const basketElement = document.getElementById(`basket-${basketToScrollTo}`);
      if (basketElement) {
        basketElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Optional: add a highlight effect
        basketElement.classList.add('highlight-basket');
        setTimeout(() => {
          basketElement.classList.remove('highlight-basket');
        }, 2000);
      }
    }
  }, [location.state?.shouldScrollToBasket, baskets]);

  // Add this effect to handle basket updates
  useEffect(() => {
    const updatedBasketId = location.state?.updatedBasketId;
    if (updatedBasketId) {
      // Find the updated basket
      const basketIndex = baskets.findIndex(b => b.id === updatedBasketId);
      if (basketIndex !== -1) {
        // Create a new array with the updated basket at the beginning
        const updatedBaskets = [...baskets];
        const [basket] = updatedBaskets.splice(basketIndex, 1);
        updatedBaskets.unshift(basket);
        
        // Update the baskets array
        dispatch(setBaskets(updatedBaskets));
        
        // Add highlight effect
        const basketElement = document.getElementById(`basket-${updatedBasketId}`);
        if (basketElement) {
          basketElement.classList.add('highlight-basket');
          setTimeout(() => {
            basketElement.classList.remove('highlight-basket');
          }, 2000);
        }
      }
    }
  }, [location.state?.updatedBasketId, baskets, dispatch]);

  // First, modify the renderBaskets function to handle study mode:
  const renderBaskets = () => {
    if (sortedBaskets.length === 0) {
      return (
        <div className="text-center py-8 sm:py-12 bg-white rounded-xl shadow-md border-2 border-neutral-200 mx-2 sm:mx-0">
          <div className="flex flex-col items-center justify-center gap-4">
            <FaShoppingBasket className="w-12 h-12 text-neutral-300" />
            <h3 className="text-lg sm:text-2xl font-semibold text-neutral-900">
              No baskets yet
            </h3>
            <p className="text-neutral-600 max-w-md mx-auto text-sm sm:text-base">
              Create your first basket to start organizing your topics.
              Click the <span className="font-medium text-cranberry-700">+</span> button in the bottom right corner.
            </p>
          </div>
        </div>
      );
    }

    if (filteredBaskets.length === 0) {
      return (
        <div className="text-center py-6 sm:py-12 bg-white rounded-xl shadow-md border-2 border-neutral-700 mx-2 sm:mx-0">
          <h3 className="text-lg sm:text-2xl font-semibold text-neutral-900 mb-2 sm:mb-4">
            No baskets found
          </h3>
          <p className="text-neutral-600 mb-4 text-sm sm:text-base">
            Try adjusting your search
          </p>
        </div>
      );
    }

    const searchTermLower = searchTerm.toLowerCase();

    // Separate baskets by match type
    const directMatches = filteredBaskets.filter(basket => 
      basket.name?.toLowerCase().includes(searchTermLower)
    );
    
    const topicMatches = filteredBaskets.filter(basket => 
      !basket.name?.toLowerCase().includes(searchTermLower) &&
      basket.topics?.some(topic => topic?.name?.toLowerCase().includes(searchTermLower))
    );
    
    const conceptMatches = filteredBaskets.filter(basket => 
      !basket.name?.toLowerCase().includes(searchTermLower) &&
      !basket.topics?.some(topic => topic?.name?.toLowerCase().includes(searchTermLower)) &&
      basket.topics?.some(topic => 
        topic?.concepts?.some(concept => 
          concept?.text?.toLowerCase().includes(searchTermLower)
        )
      )
    );

    const renderBasketTopics = (basket, matchingTopicId = null) => {
      return basket.topics?.map((topic, index) => {
        const shouldBeHidden = isStudyMode && searchTerm && (
          // Hide if basket name matches search (hide all topics)
          basket.name?.toLowerCase().includes(searchTermLower) ||
          // Or if another topic in this basket matches search
          (matchingTopicId && topic.id !== matchingTopicId)
        );

        if (shouldBeHidden && !revealedTopics.has(topic.id)) {
          return (
            <button
              key={topic.id}
              onClick={() => toggleRevealTopic(topic.id)}
              className="w-full h-12 bg-neutral-100 rounded-lg border-2 border-dashed border-neutral-300
                       hover:bg-neutral-200 transition-colors duration-200 flex items-center justify-center"
            >
              <FaEye className="w-4 h-4 text-neutral-600 mr-2" />
              <span>Reveal Topic {index + 1}</span>
            </button>
          );
        }

        return (
          <div key={topic.id} className="transition-all duration-200">
            {/* Your existing topic rendering code */}
            <div className="bg-white p-3 rounded-lg border border-neutral-200">
              <h4 className="font-medium">{topic.name}</h4>
            </div>
          </div>
        );
      });
    };

    const renderBasketSection = (baskets, title = null) => (
      <>
        {title && searchTerm && (
          <h3 className="text-sm font-medium text-neutral-500 mb-3">
            {title}
          </h3>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6">
          {baskets.map(basket => {
            const matchingTopic = isStudyMode && searchTerm 
              ? basket.topics?.find(t => t.name?.toLowerCase().includes(searchTermLower))
              : null;

            return (
              <div key={`basket-card-${basket.id}`}>
                <BasketCard 
                  id={`basket-${basket.id}`}
                  basket={{
                    ...basket,
                    topics: basket.topics?.map(topic => ({
                      ...topic,
                      is_master: topic.is_master
                    }))
                  }}
                  onClick={handleOpenBasket}
                  onMenuClick={handleBasketMenuClick}
                  searchTerm={searchTerm}
                  workspaceId={workspaceId}
                  workspaceName={workspaceName}
                  studyMode={isStudyMode}
                  onRevealBasket={() => toggleRevealBasket(basket.id)}
                  isRevealed={revealedBaskets.has(basket.id)}
                  hiddenTopics={isStudyMode ? 
                    basket.topics?.filter(t => !revealedTopics.has(t.id)).map(t => t.id) : 
                    []
                  }
                  onRevealTopic={toggleRevealTopic}
                />
              </div>
            );
          })}
        </div>
      </>
    );

    return (
      <div className="space-y-6">
        {directMatches.length > 0 && renderBasketSection(directMatches, 'Matching baskets')}
        {topicMatches.length > 0 && renderBasketSection(topicMatches, 'Matches in topic names')}
        {conceptMatches.length > 0 && renderBasketSection(conceptMatches, 'Matches in concepts')}
      </div>
    );
  };

  // Modify the renderTopics function to handle study mode:
  const renderTopics = () => {
    if (sortedTopics.length === 0) {
      return (
        <div className="text-center py-8 sm:py-12 bg-white rounded-xl shadow-md border-2 border-neutral-200 mx-2 sm:mx-0">
          <div className="flex flex-col items-center justify-center gap-4">
            <FaAppleAlt className="w-12 h-12 text-neutral-300" />
            <h3 className="text-lg sm:text-2xl font-semibold text-neutral-900">
              No items yet
            </h3>
            <p className="text-neutral-600 max-w-md mx-auto text-sm sm:text-base">
              Create your first item to start building your knowledge base.
              Click the <span className="font-medium text-cranberry-700">+</span> button in the bottom right corner.
            </p>
          </div>
        </div>
      );
    }

    if (filteredTopics.length === 0) {
      return (
        <div className="text-center py-6 sm:py-12 bg-white rounded-xl shadow-md border-2 border-neutral-700 mx-2 sm:mx-0">
          <h3 className="text-lg sm:text-2xl font-semibold text-neutral-900 mb-2 sm:mb-4">
            No items found
          </h3>
          <p className="text-neutral-600 mb-4 text-sm sm:text-base">
            Try adjusting your search
          </p>
        </div>
      );
    }

    const searchTermLower = searchTerm.toLowerCase();

    // Separate topics by match type
    const directMatches = filteredTopics.filter(topic => 
      topic.name?.toLowerCase().includes(searchTermLower)
    );

    const conceptMatches = filteredTopics.filter(topic => 
      !topic.name?.toLowerCase().includes(searchTermLower) &&
      topic.concepts?.some(concept => 
        concept?.text?.toLowerCase().includes(searchTermLower)
      )
    );

    const renderTopicGrid = (topics, title = null) => (
      <>
        {title && searchTerm && !isStudyMode && (
          <h3 className="text-sm font-medium text-neutral-500 mb-3">
            {title}
          </h3>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6">
          {topics.map(topic => {
            const isRevealed = revealedTopics.has(topic.id);

            if (isStudyMode) {
              return (
                <div key={`item-card-${topic.id}`}>
                  <StudyModeItemCard
                    topic={topic}
                    isRevealed={isRevealed}
                    onReveal={() => toggleRevealTopic(topic.id)}
                    searchTerm={searchTerm}
                  />
                </div>
              );
            }

            return (
              <div 
                key={`item-card-${topic.id}`}
                className="cursor-pointer"
                onClick={() => handleItemClick(topic)}
              >
                <ItemCard 
                  item={{
                    ...topic,
                    is_master: topic.is_master
                  }}
                  onDelete={handleDeleteItem}
                  searchTerm={searchTerm}
                  onClick={() => {}} // Handle click in parent div
                />
              </div>
            );
          })}
        </div>
      </>
    );

    return (
      <div className="space-y-6">
        {isStudyMode ? (
          // In study mode, show all matches together
          renderTopicGrid(filteredTopics)
        ) : (
          // When study mode is off, separate direct and indirect matches
          <>
            {directMatches.length > 0 && renderTopicGrid(directMatches, 'Direct matches')}
            {conceptMatches.length > 0 && renderTopicGrid(conceptMatches, 'Matches in concepts')}
          </>
        )}
      </div>
    );
  };

  // Add this new function to check for direct matches
  const hasDirectMatches = useMemo(() => {
    const hasDirectTopicMatches = filteredTopics.some(topic => 
      topic.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const hasDirectBasketMatches = filteredBaskets.some(basket => 
      basket.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return hasDirectTopicMatches || hasDirectBasketMatches;
  }, [filteredTopics, filteredBaskets, searchTerm]);

  const toggleRevealBasket = (basketId) => {
    setRevealedBaskets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(basketId)) {
        newSet.delete(basketId);
      } else {
        newSet.add(basketId);
      }
      return newSet;
    });
  };

  const toggleRevealTopic = (topicId) => {
    setRevealedTopics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
      } else {
        newSet.add(topicId);
      }
      return newSet;
    });
  };

  // Add this effect to reset revealed topics when search term changes in study mode
  useEffect(() => {
    if (isStudyMode && searchTerm) {
      setRevealedBaskets(new Set());
      setRevealedTopics(new Set());
    }
  }, [searchTerm, isStudyMode]);

  // Add this effect
  useEffect(() => {
    // Reset revealed topics when search term changes or study mode is toggled
    setRevealedTopics(new Set());
  }, [searchTerm, isStudyMode]);

  if (status === 'loading') {
    return <WorkspaceDetailSkeleton />;
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>;
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isStudyMode ? 'bg-neutral-100' : 'bg-neutral-50'
    }`}>
      {/* Header */}
      <header className="bg-neutral-50 text-neutral-900 sticky top-0 z-10 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={handleBackNavigation}
              className="text-neutral-600 hover:text-neutral-900 transition-colors duration-300 p-2"
            >
              <FaArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg sm:text-2xl font-bold text-center flex-grow mx-2 truncate">
              {workspaceName}
            </h1>
            <div className="flex items-center gap-4">
              <StudyModeSwitch 
                isStudyMode={isStudyMode}
                onChange={(checked) => {
                  setIsStudyMode(checked);
                  localStorage.setItem('isStudyMode', JSON.stringify(checked));
                  setRevealedTopics(new Set());
                  if (checked && searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </header>

      <OfflineStatusBanner />

      <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-6 max-w-6xl">
        {/* Stats now placed here, between header and search bar */}
        <div className="mb-4">
          <WorkspaceStats baskets={sortedBaskets} topics={sortedTopics} />
        </div>

        {/* Search bar */}
        <div className="mb-3 sm:mb-6">
          <div className="relative w-full max-w-2xl mx-auto">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search baskets or items..."
              className="w-full pl-9 sm:pl-12 pr-24 py-2 sm:py-3 bg-white border-2 border-neutral-700 rounded-xl 
                       focus:outline-none focus:border-neutral-900 
                       transition-all duration-200 shadow-sm text-sm sm:text-base
                       text-neutral-900 placeholder-neutral-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-neutral-400">
              <FaSearch className="w-4 h-4 sm:w-5 sm:h-5" />
            </span>
            <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
              {searchTerm && !hasDirectMatches && (
                <button
                  onClick={handleQuickCreate}
                  className="px-3 py-1 bg-cranberry-700 text-white text-sm rounded-lg border border-neutral-900 
                           hover:bg-cranberry-600 transition-colors duration-200"
                >
                  Create Item
                </button>
              )}
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="text-neutral-400 hover:text-neutral-600"
                  aria-label="Clear search"
                >
                  <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Only show tabs and filters if not in study mode */}
        <div className="flex justify-center space-x-2 sm:space-x-4 mb-3 sm:mb-6">
          {['Baskets', 'Items'].map((tab) => (
            <button
              key={tab}
              className={`px-3 sm:px-6 py-1.5 sm:py-2 rounded-xl text-sm sm:text-base font-semibold
                ${activeTab === tab
                  ? 'bg-butter-500 text-neutral-800 border-2 border-neutral-700'
                  : 'bg-white text-neutral-800 border-2 border-neutral-300 hover:border-neutral-700'
                }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab} ({tab === 'Baskets' 
                ? (searchTerm.trim() ? getFilterCounts()[BASKET_FILTERS.ALL] : sortedBaskets.length)
                : (searchTerm.trim() ? getTagFilterCounts()[ALL_TAGS_FILTER] : sortedTopics.length)
              })
            </button>
          ))}
        </div>

        {activeTab === 'Items' && (
          <ScrollableFilter 
            items={getUniqueTags}
            activeItem={activeTagFilter}
            onItemClick={(tag) => setActiveTagFilter(tag === ALL_TAGS_FILTER ? tag : tag.name)}
            getCount={(tag) => getTagFilterCounts()[tag === ALL_TAGS_FILTER ? tag : tag.name]}
            renderItem={(tag) => tag === ALL_TAGS_FILTER ? tag : (
              <span className="flex items-center gap-2">
                <span 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
              </span>
            )}
            searchInputRef={searchInputRef}
          />
        )}

        {activeTab === 'Baskets' && (
          <ScrollableFilter 
            items={Object.values(BASKET_FILTERS)}
            activeItem={basketFilter}
            onItemClick={setBasketFilter}
            getCount={(filter) => getFilterCounts()[filter]}
            renderItem={(filter) => filter.replace(' Baskets', '')}
            searchInputRef={searchInputRef}
          />
        )}

        {/* Content */}
        {activeTab === 'Baskets' ? renderBaskets() : renderTopics()}
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed left-1/2 -translate-x-1/2 bottom-24 sm:bottom-28 z-20 
                     bg-white/80 backdrop-blur-sm
                     px-4 py-2 sm:px-5 sm:py-2.5
                     rounded-full shadow-md 
                     hover:bg-white
                     transition-all duration-200 ease-in-out
                     focus:outline-none 
                     border border-neutral-200
                     group
                     scale-90 sm:scale-100
                     mx-auto
                     flex items-center gap-2"
          aria-label="Back to top"
        >
          <FaAngleDoubleUp 
            className="w-3 h-3 sm:w-4 sm:h-4 
                       text-neutral-600
                       group-hover:text-neutral-800
                       transition-colors duration-200" 
          />
          <span className="text-xs sm:text-sm text-neutral-600 group-hover:text-neutral-800 font-medium">
            Back to top
          </span>
        </button>
      )}

      {/* Mobile-optimized floating action button */}
      {workspaceId && (
        <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-20" ref={menuRef}>
          <div className="group relative">
            {/* Responsive menu */}
            <div 
              className={`
                absolute right-0 bottom-full mb-2 w-48 sm:w-56 rounded-xl shadow-lg bg-white 
                ring-1 ring-neutral-200 divide-y divide-neutral-200 border-2 border-neutral-900 
                transition-all duration-200
                ${isMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'}
                md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100
              `}
            >
              <div className="py-1">
                <button
                  className="text-neutral-900 group flex rounded-md items-center w-full px-4 py-2 text-sm font-medium 
                           transition-colors duration-150 ease-in-out hover:bg-cranberry-50 hover:text-cranberry-800"
                  onClick={() => {
                    handleAddOption('basket');
                    setIsMenuOpen(false);
                  }}
                >
                  <FaShoppingBasket className="mr-3 h-5 w-5 text-neutral-600 group-hover:text-cranberry-700" />
                  New Basket
                </button>
                <button
                  className="text-neutral-900 group flex rounded-md items-center w-full px-4 py-2 text-sm font-medium 
                           transition-colors duration-150 ease-in-out hover:bg-cranberry-50 hover:text-cranberry-800"
                  onClick={() => {
                    handleAddOption('item');
                    setIsMenuOpen(false);
                  }}
                >
                  <FaAppleAlt className="mr-3 h-5 w-5 text-neutral-600 group-hover:text-cranberry-700" />
                  New Item
                </button>
              </div>
            </div>

            {/* Fixed add button */}
            <button
              className="bg-cranberry-700 text-white p-3 sm:p-4 rounded-full shadow-lg hover:bg-cranberry-600 
                       transition duration-300 focus:outline-none focus:ring-2 focus:ring-cranberry-800 
                       focus:ring-opacity-50 border-2 border-neutral-900 group"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="relative flex items-center justify-center">
                <FaPlus 
                  className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-200 ${
                    isMenuOpen || isMenuOpen ? 'scale-0' : 'scale-100'
                  }`}
                />
                <FaChevronUp 
                  className={`w-5 h-5 sm:w-6 sm:h-6 absolute transition-all duration-200 ${
                    isMenuOpen || isMenuOpen ? 'scale-100' : 'scale-0'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      )}

      {isAddBasketModalOpen && workspaceId && (
        <AddBasketModal
          isOpen={isAddBasketModalOpen}
          onClose={() => setIsAddBasketModalOpen(false)}
          onAdd={handleAddBasket}
          workspaceId={workspaceId}
        />
      )}

      <ErrorBoundary>
        {isAddItemModalOpen && (
          <AddItemModal
            isOpen={isAddItemModalOpen}
            onClose={() => {
              setIsAddItemModalOpen(false);
              setTopicName(''); // Clear the topic name when closing
            }}
            onAdd={handleAddItem}
            workspaceId={workspaceId}
            baskets={baskets}
            initialTopicName={capitalizeFirstLetter(searchTerm)} // Capitalize here as well
          />
        )}
      </ErrorBoundary>

      {editingBasket && (
        <EditBasketModal
          basket={editingBasket}
          onClose={() => setEditingBasket(null)}
          onSave={handleSaveBasketEdit}
        />
      )}
    </div>
  );
};

export default WorkspaceDetail;
